import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ChevronDown, ChevronUp, CreditCard, DollarSign, Globe } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CounterPromoCodeBlock } from '@/components/counter/CounterPromoCodeBlock'
import { CounterCardPromoBlock } from '@/components/counter/CounterCardPromoBlock'
import { useModuleVisibility } from '@/hooks/useModuleVisibility'

export type TenderIntent = 'cash' | 'card' | 'online'

const intentMeta = {
  cash: {
    label: 'Cash',
    Icon: DollarSign,
    active:
      'border-emerald-600 bg-emerald-600 text-white shadow-sm shadow-emerald-900/20 ring-1 ring-emerald-500/30',
    idle:
      'border-emerald-200 bg-emerald-50/80 text-emerald-900 hover:border-emerald-300 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-50 dark:hover:bg-emerald-900/50',
  },
  card: {
    label: 'Card',
    Icon: CreditCard,
    active:
      'border-sky-600 bg-sky-600 text-white shadow-sm shadow-sky-900/20 ring-1 ring-sky-400/35',
    idle:
      'border-sky-200 bg-sky-50/80 text-sky-950 hover:border-sky-300 hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-50 dark:hover:bg-sky-900/50',
  },
  online: {
    label: 'Online',
    Icon: Globe,
    active:
      'border-violet-600 bg-violet-600 text-white shadow-sm shadow-violet-900/25 ring-1 ring-violet-400/35',
    idle:
      'border-violet-200 bg-violet-50/85 text-violet-950 hover:border-violet-300 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-50 dark:hover:bg-violet-900/50',
  },
} as const

export interface TenderInputProps {
  intent: TenderIntent
  onIntent: (i: TenderIntent) => void
  discountMode: 'amount' | 'percent'
  onDiscountModeChange: (mode: 'amount' | 'percent') => void
  discountValue: string
  onDiscountValueChange: (v: string) => void
  discountPending: boolean
  onApplyDiscount: () => void
  /** Order id used by the inline promo redemption block. */
  orderId: string
  /** Forwarded to the promo block — totals shift on apply/remove. */
  onPromoChanged?: () => void
  /**
   * Defaults the discount/promo accordion to open when the order already
   * carries a manual discount or an applied promo (both write to
   * `discount_amount` server-side, so one flag covers both).
   */
  hasExistingDiscount?: boolean
}

export function TenderInput({
  intent,
  onIntent,
  discountMode,
  onDiscountModeChange,
  discountValue,
  onDiscountValueChange,
  discountPending,
  onApplyDiscount,
  orderId,
  onPromoChanged,
  hasExistingDiscount,
}: TenderInputProps) {
  const [discountOpen, setDiscountOpen] = useState<boolean>(Boolean(hasExistingDiscount))
  // Promo codes and bank card promos are Marketing Suite features — hidden at
  // the till on deployments that hide the suite. Manual discounts stay.
  const marketingOn = useModuleVisibility().isEnabled('marketing')
  return (
    <section
      aria-labelledby="tender-input-heading"
      className="space-y-3 rounded-lg border border-border bg-card/50 p-3"
    >
      <div>
        <h3
          id="tender-input-heading"
          className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
        >
          Method
        </h3>
        <div className="mt-2 grid grid-cols-3 gap-1.5">
          {(['cash', 'card', 'online'] as const).map((key) => {
            const meta = intentMeta[key]
            const Icon = meta.Icon
            const on = intent === key
            return (
              <button
                key={key}
                type="button"
                onClick={() => onIntent(key)}
                className={cn(
                  'flex h-11 flex-col items-center justify-center gap-0 rounded-lg border px-1 text-[11px] font-semibold uppercase tracking-wide transition-colors sm:h-12 sm:text-xs',
                  on ? meta.active : meta.idle
                )}
                aria-pressed={on}
              >
                <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
                {meta.label}
              </button>
            )
          })}
        </div>
        {/* Bank card promo quick-pick — directly under the tender buttons
            (not inside the collapsed Discount panel): the cashier taps
            Card, then taps the matching promo chip. Visible only on the
            card intent; the backend enforces the same condition. */}
        {marketingOn && (
          <div className="mt-2 empty:mt-0">
            <CounterCardPromoBlock
              orderId={orderId}
              visible={intent === 'card'}
              onChanged={onPromoChanged}
            />
          </div>
        )}
      </div>

      <div>
        <button
          type="button"
          onClick={() => setDiscountOpen((v) => !v)}
          aria-expanded={discountOpen}
          aria-controls="counter-discount-panel"
          className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-background/40 px-3 py-2 text-left transition-colors hover:bg-muted/40"
        >
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Discount
          </span>
          {discountOpen ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" aria-hidden />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
          )}
        </button>
        {discountOpen && (
          <div id="counter-discount-panel" className="mt-3 space-y-3">
            <div className="space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Discount (whole bill)
              </div>
              <div className="flex flex-wrap gap-2">
                <div className="grid shrink-0 grid-cols-2 gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant={discountMode === 'amount' ? 'default' : 'outline'}
                    className="h-9 px-2 text-sm"
                    onClick={() => onDiscountModeChange('amount')}
                    aria-pressed={discountMode === 'amount'}
                  >
                    Rs
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={discountMode === 'percent' ? 'default' : 'outline'}
                    className="h-9 px-2 text-sm"
                    onClick={() => onDiscountModeChange('percent')}
                    aria-pressed={discountMode === 'percent'}
                  >
                    %
                  </Button>
                </div>
                <Input
                  inputMode="decimal"
                  className="h-9 min-w-[6rem] flex-1 text-sm"
                  placeholder={discountMode === 'percent' ? 'e.g. 10' : '0.00'}
                  value={discountValue}
                  onChange={(e) => onDiscountValueChange(e.target.value)}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="h-9 shrink-0 px-3 text-sm"
                  disabled={discountPending}
                  onClick={onApplyDiscount}
                >
                  Apply
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">Enter 0 and apply to clear discount.</p>
            </div>

            {marketingOn && (
              <CounterPromoCodeBlock orderId={orderId} onChanged={onPromoChanged} />
            )}
          </div>
        )}
      </div>
    </section>
  )
}
